import React, { useState, useEffect, useCallback } from 'react';
import { BoardContainer, BoardHeader, MachineSelector, WeekdaysHeader, DayColumn, BoardGrid, MachineRow, MachineCell, DayCell, OPCard } from './style';
import FashionApiService from '../../../../services/FashionApiService';
import axios from '../../../../config/axios';
import { FashionProductionOrderData } from '../FashionPcp';

interface Machine {
    id: number;
    code: string;
    name: string;
}

const WEEKDAYS = [
    { short: 'Segunda', full: 'Segunda' },
    { short: 'Terça', full: 'Terça' },
    { short: 'Quarta', full: 'Quarta' },
    { short: 'Quinta', full: 'Quinta' },
    { short: 'Sexta', full: 'Sexta' }
];

const FashionPcpBoard: React.FC = () => {
    const [machines, setMachines] = useState<Machine[]>([]);
    const [productionOrders, setProductionOrders] = useState<FashionProductionOrderData[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const [machinesResponse, ordersResponse] = await Promise.all([
                axios.get('/equipment/equipment'),
                FashionApiService.getProductionOrders()
            ]);

            setMachines(machinesResponse.data || []);
            setProductionOrders(ordersResponse.data || []);
        } catch (err: any) {
            console.error('Erro ao buscar dados:', err);
            setError('Não foi possível carregar os dados.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const getOPsForMachineAndDay = (machineId: number, dayFull: string): FashionProductionOrderData[] => {
        const ops = productionOrders.filter(op =>
            op.machineId === machineId && op.productionDay === dayFull
        );

        const priorityOrder: { [key: string]: number } = {
            'Alta': 1,
            'Média': 2,
            'Baixa': 3
        };

        return ops.sort((a, b) => {
            const priorityA = priorityOrder[a.priority || ''] || 999;
            const priorityB = priorityOrder[b.priority || ''] || 999;
            return priorityA - priorityB;
        });
    };

    if (isLoading) {
        return <p>Carregando...</p>;
    }

    if (error) {
        return <p style={{ color: 'red' }}>Erro: {error}</p>;
    }

    return (
        <BoardContainer>
            <BoardHeader>
                <MachineSelector></MachineSelector>
                <WeekdaysHeader>
                    {WEEKDAYS.map(day => (
                        <DayColumn key={day.short}>{day.short}-feira</DayColumn>
                    ))}
                </WeekdaysHeader>
            </BoardHeader>

            <BoardGrid>
                {machines.map(machine => (
                    <MachineRow key={machine.id}>
                        <MachineCell>{machine.code}</MachineCell>
                        {WEEKDAYS.map(day => {
                            const ops = getOPsForMachineAndDay(machine.id, day.full);
                            return (
                                <DayCell key={`${machine.id}-${day.short}`}>
                                    {ops.map(op => (
                                        <OPCard key={op.id}>
                                            OP{String(op.id).padStart(4, '0')}
                                        </OPCard>
                                    ))}
                                </DayCell>
                            );
                        })}
                    </MachineRow>
                ))}
            </BoardGrid>
        </BoardContainer>
    );
};

export default FashionPcpBoard;
